/**
 * Module 'control': "Control every session" on a machine (ADR 0001, amended 17 Oct 2026;
 * docs/architecture/session-control.md). With control on, the machine's agent has `http`
 * hooks in the user-level Claude Code settings, so approvals, budget stops, policy and
 * messages reach every Claude Code session there, not only the ones Orgus launched.
 *
 *   agent -> hub  'status'   ControlMachineStatus, whenever it changes
 *   hub -> agent  'set'      ControlSetRequest (id); the agent answers 'result' with that id
 *   hub -> agent  'message'  SessionMessageRequest (id); answered with 'result'
 *   agent -> hub  'result'   ControlResult
 *   hub -> agent  'note'     ControlNote (id): a note for a discovered session, injected at its next
 *                            UserPromptSubmit or Stop hook (G3, ADR 0056 amended 21 Oct 2026); 'result'
 *   agent -> hub  'note'     ControlNoteEvent: a note was delivered, its session stayed idle, or it was dropped
 *
 * Browser-safe and pure.
 */
import type { Id, Ts } from '../index.ts';

/** The oldest Claude Code with `http` hooks (third-party changelogs; verified working on 2.1.302). */
export const CONTROL_MIN_CLAUDE_VERSION = '2.1.63';

/** Bumped when the hooks Orgus installs change shape; status shows when an install is older. */
export const CONTROL_INSTALL_FORMAT = 2;

/** How long a PermissionRequest waits for a person in Orgus before Claude Code asks in the session. */
export const CONTROL_DEFAULT_WAIT_SECONDS = 120;
export const CONTROL_MAX_WAIT_SECONDS = 3600;
/**
 * The most a terminal session (entrypoint cli) is held: its own prompt appears only after the
 * hook answers, so a person at the terminal would otherwise wait out the whole wait.
 */
export const CONTROL_TERMINAL_WAIT_SECONDS = 20;

/**
 * Where a terminal session (entrypoint cli) asks first. 'terminal': its own prompt after at most
 * CONTROL_TERMINAL_WAIT_SECONDS. 'orgus': held in Orgus for the full wait, for a person away
 * from the terminal (its prompt shows only if nobody answers in Orgus).
 */
export type ControlTerminalFirst = 'terminal' | 'orgus';
export const CONTROL_DEFAULT_TERMINAL_FIRST: ControlTerminalFirst = 'terminal';

/** Longest message Orgus sends into a session. */
export const SESSION_MESSAGE_MAX_CHARS = 4000;

/** The hook events Orgus installs (see the research table in session-control.md). */
export const CONTROL_HOOK_EVENTS = [
  'PreToolUse',
  'PermissionRequest',
  'Elicitation',
  'UserPromptSubmit',
  'Notification',
  'Stop',
  'SessionEnd',
  'PostModelSwitch',
  'PreCompact',
] as const;
export type ControlHookEvent = (typeof CONTROL_HOOK_EVENTS)[number];

/** Why a live session on a controlled machine is not controlled (yet). */
export type UncontrolledReason =
  /** It started before control was turned on and has not called Orgus since. */
  | 'started-before'
  /** It started after, but no hook call has arrived (another settings folder, hooks off, workspace not trusted yet). */
  | 'no-hook-yet';

export interface ControlledSession {
  sessionId: Id;
  pid?: number;
  cwd?: string;
  startedAt?: Ts;
  /** A hook call from this session reached the agent since control was turned on. */
  controlled: boolean;
  why?: UncontrolledReason;
  lastHookAt?: Ts;
  /** Claude Code's version, from its process record. */
  version?: string;
  /** Orgus launched it: it carries its own per-session hooks. */
  launched?: boolean;
}

export type ControlSupport =
  | { ok: true; version: string }
  | { ok: false; version: string | null; reason: string };

/** One machine's control, as its agent reports it. */
export interface ControlMachineStatus {
  /** Hooks are in the settings file (control is on). */
  installed: boolean;
  /** The settings file Orgus manages (user level, or $CLAUDE_CONFIG_DIR). */
  settingsFile: string;
  installedAt?: Ts;
  /** CONTROL_INSTALL_FORMAT of the install; older than the agent's means "turn it off and on". */
  format?: number;
  /** Claude Code on this machine, and whether it has `http` hooks. */
  support: ControlSupport;
  /** The endpoint the hooks call; null while it is not listening. */
  port: number | null;
  listening: boolean;
  /** Why the endpoint is not listening while control is on (port taken, ...). */
  problem?: string;
  waitSeconds: number;
  /** Absent from agents older than install format 2: 'terminal'. */
  terminalFirst?: ControlTerminalFirst;
  /** Live Claude Code sessions on the machine, controlled or not. */
  sessions: ControlledSession[];
  /** Hooks that would switch Orgus's off, when visible in the user file (disableAllHooks). */
  warnings: string[];
  at: Ts;
}

export interface ControlMachineState extends ControlMachineStatus {
  machineId: Id;
  /** The machine is connected; when false this is the last status it sent. */
  online: boolean;
}

/** state.ext.control */
export interface ControlState {
  machines: Record<Id, ControlMachineState>;
}

/** POST /api/control/:machineId, and hub -> agent 'set'. */
export interface ControlSetRequest {
  on: boolean;
  waitSeconds?: number;
  terminalFirst?: ControlTerminalFirst;
}

/** POST /api/control/:machineId/message, and hub -> agent 'message'. */
export interface SessionMessageRequest {
  sessionId: Id;
  /** May be empty when files are sent. */
  text: string;
  /**
   * Console to hub only: uploads that go with the message. The hub copies them to the machine
   * and adds their paths to `text`, so the agent never sees this field.
   */
  attachmentIds?: Id[];
}

export interface ControlResult {
  ok: boolean;
  /** What happened, in a sentence for people. */
  message: string;
  /** For refusals: a short code ('unsupported', 'malformed', 'busy', 'not-found', ...). */
  code?: string;
  status?: ControlMachineStatus;
}

/**
 * A landing-queue notice for a session Orgus did not launch, on a machine with session control on
 * (G3): the hub's wake or landed notice, held by the machine's agent until the session's next
 * hook event that can carry text (UserPromptSubmit: additionalContext; Stop: a block reason for a
 * rejection, a message to its person for a landing). At most one per session per branch: a newer
 * one replaces it, and a landed one also drops the branch's notes for other sessions.
 */
export interface ControlNote {
  sessionId: Id;
  /** The branch it is about ('' when the wake did not say). */
  branch: string;
  kind: ControlNoteKind;
  text: string;
  at: Ts;
}
export type ControlNoteKind = 'rejected' | 'landed';

/** agent -> hub 'note': what became of a note. */
export interface ControlNoteEvent {
  sessionId: Id;
  branch: string;
  kind: ControlNoteKind;
  event: 'delivered' | 'idle' | 'dropped';
  at: Ts;
  /** delivered: the hook that carried it. dropped: why ('ended', 'replaced', 'landed', 'control-off'). */
  detail?: string;
  /** idle: since when the session made no hook call. */
  idleSince?: Ts;
}

/**
 * How long a session with a pending rejection note may make no hook call before its person is
 * told too (console and phone, module notify), with the branch and the fix.
 */
export const CONTROL_NOTE_IDLE_MINUTES = 20;
export const CONTROL_NOTE_IDLE_MS = CONTROL_NOTE_IDLE_MINUTES * 60_000;
/** Longest note text: a hook's additionalContext and block reason stay readable. */
export const CONTROL_NOTE_MAX_CHARS = 8000;

/** Audit labels for what this module records (their `action` is not an ActionKind). */
export const CONTROL_AUDIT_LABEL: Record<string, string> = {
  'control.on': 'Turn on session control',
  'control.off': 'Turn off session control',
  'session.message': 'Message session',
  'session.note': 'Note for session',
};

/** "2.1.302 (Claude Code)" -> "2.1.302"; null when there is no version in it. */
export function parseClaudeVersion(text: string): string | null {
  const m = /(\d+\.\d+\.\d+)/.exec(text);
  return m ? m[1]! : null;
}

/** Sentence for a live session on a controlled machine that is not controlled. */
export function uncontrolledText(why: UncontrolledReason | undefined): string {
  return why === 'started-before'
    ? 'Not controlled yet: it started before control was turned on. It is controlled from its next prompt or tool call if it uses this machine\'s settings folder.'
    : 'Not controlled yet: no hook call has reached Orgus from it. It may use another settings folder, have hooks turned off, or wait for its folder to be trusted.';
}
